import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";

import ContentItem from "../../../../components/layout/content/ContentItem";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";

import TalentAvatar from "../avatar/TalentAvatar";

import "./SharedTalentCard.css";

const clean = (value) => String(value ?? "").trim();

const getLocation = (talent) =>
  [talent?.state, talent?.country].map(clean).filter(Boolean).join(", ");

export default function SharedTalentCard({ talent }) {
  if (!talent) return null;

  const talentTag = clean(talent.talentTag);
  const name = clean(talent.name) || "Talent Profile";

  const primarySport =
    talent.sports?.find((sport) => Boolean(sport.isPrimary)) ||
    talent.sports?.[0];

  const sport = clean(
    talent.primarySport || primarySport?.sport || talent.sport,
  );

  const role = clean(talent.role || primarySport?.role);

  const position = clean(
    talent.mainPosition ||
      primarySport?.mainPosition ||
      talent.position ||
      talent.otherPosition,
  );

  const location = getLocation(talent);

  const availability = clean(talent.availability || primarySport?.availability);

  const isAvailable = availability.toLowerCase() === "available";

  const subtitle = [sport, role].filter(Boolean).join(" · ");

  const content = (
    <ContentItem className="shared-talent-card__item">
      <div className="shared-talent-card__layout">
        <TalentAvatar profile={talent} size="medium" />

        <div className="shared-talent-card__content">
          <div className="shared-talent-card__heading">
            <Heading as="h3" size="sm">
              {name}
            </Heading>

            {talentTag && (
              <ChevronRight
                size={18}
                className="shared-talent-card__arrow"
                aria-hidden="true"
              />
            )}
          </div>

          {subtitle && (
            <Text as="p" tone="secondary" size="sm">
              {subtitle}
            </Text>
          )}

          {position && (
            <Text as="p" size="sm" className="shared-talent-card__position">
              {position}
            </Text>
          )}

          {(location || availability) && (
            <div className="shared-talent-card__meta">
              {location && (
                <div className="shared-talent-card__location">
                  <MapPin size={13} aria-hidden="true" />

                  <Text as="span" tone="secondary" size="xs">
                    {location}
                  </Text>
                </div>
              )}

              {availability && (
                <div className="shared-talent-card__availability">
                  <span
                    className={[
                      "shared-talent-card__status",
                      isAvailable && "shared-talent-card__status--available",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-hidden="true"
                  />

                  <Text as="span" tone="secondary" size="xs">
                    {availability}
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ContentItem>
  );

  return (
    <article className="shared-talent-card">
      {talentTag ? (
        <Link
          to={`/talents/${encodeURIComponent(talentTag)}`}
          className="shared-talent-card__link"
          aria-label={`View ${name}'s Talent Profile`}
        >
          {content}
        </Link>
      ) : (
        <div className="shared-talent-card__link shared-talent-card__link--disabled">
          {content}
        </div>
      )}
    </article>
  );
}
